import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AthleteItem } from '../services/api';
import { coachDashboard } from '../services/api';
import {
  athleteTrendColor,
  clipPointsToIsoRange,
  mapRecordsToSeriesPoints,
  selectTopPeakPoints,
  type PeakTrendSeries,
} from '../utils/coachPeakTrends';
import {
  resolveRecordHistoryParams,
  type PeakCategory,
  type RecordMetricType,
  type RecordRangeId,
} from '../utils/recordHistoryEnums';

export interface CoachPeakTrendsFilters {
  category: PeakCategory;
  peakType: string;
  rangeId: RecordRangeId;
  sportId: string;
  metricType: RecordMetricType;
  customStartDate: string;
  customEndDate: string;
  customRangeApplied: boolean;
}

interface UseCoachPeakTrendsOptions {
  athletes: AthleteItem[];
  filters: CoachPeakTrendsFilters;
  refreshKey: number;
}

const COACH_TRENDS_CACHE_TTL_MS = 2 * 60 * 1000;

export function useCoachPeakTrends({ athletes, filters, refreshKey }: UseCoachPeakTrendsOptions) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [seriesByAthlete, setSeriesByAthlete] = useState<Record<number, PeakTrendSeries>>({});
  const requestGenerationRef = useRef(0);
  const cacheRef = useRef(new Map<string, { ts: number; value: Record<number, PeakTrendSeries> }>());
  const athleteIdsKey = useMemo(
    () => athletes.map((athlete) => athlete.id).sort((a, b) => a - b).join(','),
    [athletes],
  );
  const isPowerPeak = filters.category === 'power';

  const load = useCallback(async () => {
    const generation = ++requestGenerationRef.current;
    if (!athletes.length) {
      setSeriesByAthlete({});
      setLoading(false);
      setError(false);
      return;
    }

    const customInvalid =
      filters.rangeId === 'custom' &&
      (!filters.customRangeApplied ||
        !filters.customStartDate ||
        !filters.customEndDate ||
        filters.customStartDate > filters.customEndDate);
    if (customInvalid) {
      setSeriesByAthlete({});
      setLoading(false);
      setError(false);
      return;
    }

    const params = resolveRecordHistoryParams(
      filters.rangeId,
      filters.peakType,
      filters.sportId,
      filters.metricType,
      filters.rangeId === 'custom'
        ? { start: filters.customStartDate, end: filters.customEndDate }
        : undefined,
    );
    params.limit = 10;
    const rangeStart = typeof params.start_date === 'string' ? params.start_date : undefined;
    const rangeEnd = typeof params.end_date === 'string' ? params.end_date : undefined;
    const cacheKey = JSON.stringify([athleteIdsKey, filters, params]);
    const cached = cacheRef.current.get(cacheKey);

    if (cached && Date.now() - cached.ts < COACH_TRENDS_CACHE_TTL_MS) {
      setSeriesByAthlete(cached.value);
      setLoading(false);
      setError(false);
      return;
    }

    try {
      setLoading(true);
      setError(false);
      const response = await coachDashboard.getPeakTrends(params);
      if (generation !== requestGenerationRef.current) return;

      const payloadById = new Map(
        (response.data?.data?.athletes ?? []).map((item) => [item.athlete_id, item]),
      );
      const next: Record<number, PeakTrendSeries> = {};
      athletes.forEach((athlete, index) => {
        const payload = payloadById.get(athlete.id);
        const color = athleteTrendColor(athlete.id, index);
        next[athlete.id] = {
          athleteId: athlete.id,
          athleteName: athlete.full_name,
          color,
          points: selectTopPeakPoints(
            clipPointsToIsoRange(
              mapRecordsToSeriesPoints(payload?.records ?? [], filters.metricType, isPowerPeak, {
                athleteId: athlete.id,
                athleteName: athlete.full_name,
                color,
              }),
              rangeStart,
              rangeEnd,
            ),
            filters.category,
          ),
        };
      });

      setSeriesByAthlete(next);
      cacheRef.current.set(cacheKey, { ts: Date.now(), value: next });
      if (cacheRef.current.size > 5) {
        const oldestKey = cacheRef.current.keys().next().value;
        if (oldestKey) cacheRef.current.delete(oldestKey);
      }
    } catch {
      if (generation !== requestGenerationRef.current) return;
      setSeriesByAthlete({});
      setError(true);
    } finally {
      if (generation === requestGenerationRef.current) setLoading(false);
    }
  }, [
    athleteIdsKey,
    athletes,
    filters,
    filters.category,
    filters.customEndDate,
    filters.customRangeApplied,
    filters.customStartDate,
    filters.metricType,
    filters.peakType,
    filters.rangeId,
    filters.sportId,
    isPowerPeak,
  ]);

  useEffect(() => {
    void load();
    return () => {
      requestGenerationRef.current += 1;
    };
  }, [load, refreshKey]);

  const series = useMemo(
    () => athletes.map((athlete) => seriesByAthlete[athlete.id]).filter(Boolean),
    [athletes, seriesByAthlete],
  ) as PeakTrendSeries[];

  return {
    series,
    loading,
    error,
    hasAnyPoints: series.some((item) => item.points.length > 0),
    reload: load,
  };
}
